<template>
  <v-text-field
    outlined
    dense
    hide-details="auto"
    :placeholder="placeholder"
    @input="lazySearchRecords"
    @keydown.enter="lazySearchRecords($event.target.value)"
    :class="`btn-light-grey-border debounce-serach-field ${textFieldClass}`"
  >
    <template #append>
      <v-icon class="material-icons text--secondary">{{ fieldIcon }}</v-icon>
    </template>
  </v-text-field>
</template>

<script>
import debounce from "lodash/debounce";
import {
  DEBOUNCE_MILLIS,
  SORTING_ORDERS,
  TABLE_PER_PAGES_OPTIONS,
} from "@/constants";

/**
 * Search field to be used to lazy search the records
 * @author {Jatin Kamboj}
 */
export default {
  name: "DebounceSearchTextField",
  /**
   * ---------------- Custom events ------------------
   */
  emits: ["search-value"],
  /**
   * ---------------- Props ------------------
   */
  props: {
    /**
     * Placeholder of the textfield
     */
    placeholder: { type: String, default: "", required: true },
    /**
     * Prepend icon of the input textfield
     */
    fieldIcon: { type: String, default: "search" },
    /**
     * Function to search the records and which is to be debounced
     */
    lazySearch: { type: Function, required: true },
    /**
     * Property name which is to be used in request params for searching a record
     */
    propToSearch: { type: String, required: true, default: "" },
    /**
     * Classes to apply to textfield
     */
    textFieldClass: { type: String, default: "mr-4" },
    /**
     * No of records to be fetched from the api resource
     */
    noOfRecords: {
      type: [Number, String],
      default: TABLE_PER_PAGES_OPTIONS[1],
    },
    /**
     * Name of the property by which records are to be fetched from server (api) resource
     */
    sortBy: { type: String, default: "" },
    /**
     * Sort order of the record by which sorted records are to be fetched
     */
    sortOrder: { type: String, default: SORTING_ORDERS.asc },
    /**
     * Custom search query param
     */
    customSearchParam: { type: String, default: "" },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    /**
     * Lazy loads the records
     * @description Debounce function which gets execeuted after 500 milli seconds of delay
     */
    lazySearchRecords: debounce(function(val) {
      const params = { page: 1, per_page: this.noOfRecords };
      // Sorting query param
      params[`q[s]`] = `${this.sortBy ?? ""} ${this.sortOrder}`;

      // Search query param
      this.setSearchParam(params, val);

      this.updateText(val);
      this.lazySearch(params);
    }, DEBOUNCE_MILLIS),
    /**
     * @emits search-value event with current search input field value
     */
    updateText(val) {
      this.$emit("search-value", val);
    },
    /**
     * Sets custom search query param in the request payload
     * @param {Object} params Request params
     * @param {String} val Value of the input text field
     */
    setSearchParam(params, val) {
      if (this.customSearchParam) params[this.customSearchParam] = val;
      else params[`q[${this.propToSearch}_cont]`] = val;
    },
  },
};
</script>
